// A dynamic service line is named after the rule that priced it, like a Bierdeckel
// position — until the user types a name of their own. The line remembers the name the
// wizard last wrote (`autoName`); as long as `name` still equals it, a re-evaluation may
// rename it, otherwise the name is the user's and stays. Same idea as the berth lines'
// seededSignature. (#7044)

import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';

import { BookingV2Service } from '../dtos/booking-v2.dtos';

export function serviceFollowsAutoName(service: Pick<BookingV2Service, 'name' | 'autoName'>): boolean {
    return service.autoName != null && service.name === service.autoName;
}

// Writes an evaluation onto the line; renames it only while the name is the wizard's own.
export function withEvaluatedServicePrice(
    service: BookingV2Service,
    result: { price: number; ruleName: string | null; dynamicPrice: Partial<BcmDynamicPriceDto> | null },
): BookingV2Service {
    const next: BookingV2Service = {
        ...service,
        price: result.price,
        dynamicRuleName: result.ruleName,
        dynamicPrice: result.dynamicPrice,
    };
    if (serviceFollowsAutoName(service) && result.ruleName) {
        next.name = result.ruleName;
        next.autoName = result.ruleName;
    }
    return next;
}

// Edit load: a saved title that is the rule's or the product's name was the wizard's
// own and may follow the rule again; anything else was typed and is kept (`null`).
export function recoverServiceAutoName(
    title: string | null | undefined,
    ruleName: string | null | undefined,
    productName: string | null | undefined,
): string | null {
    if (!title) {
        return null;
    }
    return title === ruleName || title === productName ? title : null;
}

// The name a line gets from its editor. Emptied, it goes back to the current rule name
// (else the name the wizard last wrote) and follows the rule again; typing exactly that
// name follows it too; anything else is the user's own.
export function editedServiceName(
    service: Pick<BookingV2Service, 'name' | 'autoName' | 'dynamicRuleName'>,
    typed: string,
): Pick<BookingV2Service, 'name' | 'autoName'> {
    const ruleName = service.dynamicRuleName || service.autoName || null;
    if (!typed) {
        const name = ruleName ?? service.name;
        return { name, autoName: name };
    }
    // autoName ≠ name marks the typed name as the user's own.
    return { name: typed, autoName: ruleName };
}
